/**
 * Shared upload rules. These live in `lib` because both sides must agree
 * exactly: the browser refuses a bad file before spending time uploading it,
 * and the API refuses the same file because the browser cannot be trusted.
 */

/**
 * Content types accepted for a document.
 *
 * SVG is deliberately absent. It is a script-bearing format, and a stored SVG
 * served back to a browser is a cross-site-scripting vector; PNG or PDF covers
 * every legitimate map or plan we would receive.
 */
export const ALLOWED_DOCUMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/tiff',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
] as const

export type AllowedDocumentContentType =
  (typeof ALLOWED_DOCUMENT_CONTENT_TYPES)[number]

/**
 * A type guard rather than a plain boolean check, so a caller that has narrowed
 * a `File.type` down to an allowed value can pass it straight into the input
 * schema without an assertion. Returning `boolean` here would force every call
 * site into an `as` cast — which is the thing the guard exists to avoid.
 */
export function isAllowedDocumentContentType(
  value: string,
): value is AllowedDocumentContentType {
  const allowed: readonly string[] = ALLOWED_DOCUMENT_CONTENT_TYPES
  return allowed.includes(value)
}

/**
 * 50 MB. Site maps are large multi-layer PDFs — the one for The Trails at Regal
 * Park is under a megabyte, but a scanned plan set is not — while still being
 * small enough that a failed upload doesn't waste someone's afternoon.
 */
export const MAX_DOCUMENT_BYTES = 50 * 1024 * 1024

/** How long an upload or download link stays valid. */
export const DOCUMENT_URL_TTL_SECONDS = 15 * 60

/** Human-readable file size, for UI labels. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unitIndex = 0
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10
  return `${rounded} ${units[unitIndex]}`
}

/**
 * Every column a document can belong to. At most one is set; none means the
 * reusable library.
 *
 * One list, because the library is defined by absence: it is the documents
 * where every one of these is null. An owner added to the upload check but
 * forgotten in that filter would put its photos into "Stock photos", so the
 * API builds both from here.
 */
export const DOCUMENT_OWNER_KEYS = [
  'clientId',
  'siteId',
  'treeId',
  'treeProblemId',
  'waterSourceId',
  'irrigationTimerId',
  'irrigationEquipmentId',
  'irrigationProblemId',
] as const

export type DocumentOwnerKey = (typeof DOCUMENT_OWNER_KEYS)[number]

/**
 * Every column a video can belong to. At most one is set; none means a stock
 * clip for a problem type.
 *
 * One list for the reason `DOCUMENT_OWNER_KEYS` is one, with a sharper edge:
 * stock clips nothing points at are swept after a day, and "stock" is every
 * one of these being null. An owner missing from here would have its videos
 * deleted, so the sweep and the explainer check are built from this list.
 */
export const VIDEO_OWNER_KEYS = [
  'treeId',
  'waterSourceId',
  'irrigationTimerId',
  'irrigationEquipmentId',
  'irrigationProblemId',
] as const

export type VideoOwnerKey = (typeof VIDEO_OWNER_KEYS)[number]

/**
 * Content types a TREE PHOTO may be.
 *
 * A narrower list than a document's, because this one has a camera behind it
 * rather than a file picker. HEIC is here because it is what an iPhone produces
 * by default; PDF and the office formats are not, because a spreadsheet is not
 * a photograph of a tree and accepting one would let the survey screen quietly
 * become a second document library.
 */
export const ALLOWED_PHOTO_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const

export type AllowedPhotoContentType =
  (typeof ALLOWED_PHOTO_CONTENT_TYPES)[number]

export function isAllowedPhotoContentType(
  value: string,
): value is AllowedPhotoContentType {
  const allowed: readonly string[] = ALLOWED_PHOTO_CONTENT_TYPES
  return allowed.includes(value)
}

/**
 * The cap on one photo, well under `MAX_DOCUMENT_BYTES`.
 *
 * A modern phone camera produces 3–8 MB a frame, and a surveyor uploading over
 * a rural cell connection pays for every one. Twenty megabytes leaves room for
 * a high-resolution capture while still refusing the video somebody took by
 * holding the shutter down.
 */
export const MAX_PHOTO_BYTES = 20 * 1024 * 1024

/**
 * The longest tree video worth storing.
 *
 * Five minutes. A surveyor walking round a trunk narrating what they see takes
 * well under a minute; anything past this is a phone left recording in a
 * pocket, and Stream bills by the minute both to store and to deliver.
 */
export const MAX_VIDEO_SECONDS = 5 * 60

/**
 * Content types an ORGANIZATION LOGO may be.
 *
 * Narrower than a photo's, and for a different reason. HEIC is excluded
 * because no browser renders it and a logo's whole job is to be rendered — in
 * the app, on a proposal a client opens, and in a print dialog. PDF is
 * excluded because a logo is an image, not a document.
 *
 * SVG stays out for the reason it is out everywhere here: it carries script,
 * and this is the one image the app serves from an UNAUTHENTICATED route. A
 * stored SVG returned to a browser would be a cross-site-scripting vector
 * reachable without a login.
 */
export const ALLOWED_LOGO_CONTENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
] as const

/**
 * 2 MB, far below a document's cap.
 *
 * A logo is a small flat image that renders a few centimetres wide. Anything
 * larger is a print-resolution asset that will be downscaled anyway — and it
 * is loaded by every client opening a proposal, some of them on a phone.
 */
export const MAX_LOGO_BYTES = 2 * 1024 * 1024
